import React, { Component } from 'react'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import ListItemText from '@material-ui/core/ListItemText'
import Tab from '@material-ui/core/Tab'
import Tabs from '@material-ui/core/Tabs'
import Typography from '@material-ui/core/Typography'
import Collapse from '@material-ui/core/Collapse'
import IconButton from '@material-ui/core/IconButton'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { UDV_SURFACE, udvDialogProps } from './udvSurface'
import { t } from '../i18n'
import {
  formatWorklistSessionTitle,
  groupWorklistSessionsByDay,
} from '../db/worklistSessions'

function formatDate(dcm) {
  const s = String(dcm || '')
  if (s.length >= 8) {
    return `${s.slice(6, 8)}-${s.slice(4, 6)}-${s.slice(0, 4)}`
  }
  return s || '—'
}

function formatTime(ts) {
  try {
    return new Date(ts).toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch (e) {
    return ''
  }
}

/**
 * Modal Worklist: Cola (sesión actual) | Recientes (sesiones IndexedDB).
 *
 * Props:
 *   open, onClose,
 *   tab: 'cola'|'recientes',
 *   onTabChange: (tab) => void,
 *   items: worklist items[],
 *   title?: string|null,
 *   currentTicketId?: string|null,
 *   localByUid?: object,
 *   sessions: worklist_sessions[],
 *   onOpenItem: (ticketId) => void,
 *   onOpenLocal: (studyInstanceUid, item?) => void,
 *   onManageStorage: () => void,
 */
class WorklistDlg extends Component {
  constructor(props) {
    super(props)
    this.state = {
      expandedId: null,
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.setState({ expandedId: null })
    }
  }

  isInLocal = (item) => {
    if (!item || !item.studyInstanceUid) return false
    const map = this.props.localByUid
    if (!map || typeof map !== 'object') return false
    return !!map[String(item.studyInstanceUid)]
  }

  isCurrentItem = (item) => {
    if (!item) return false
    if (item.isCurrent) return true
    const cur = this.props.currentTicketId
    return !!(cur && item.ticketId === String(cur))
  }

  handleOpen = (item) => {
    if (!item) return
    if (typeof this.props.onOpenItem === 'function' && item.ticketId) {
      this.props.onOpenItem(item.ticketId)
    }
  }

  handleOpenLocal = (item) => {
    if (!item || !item.studyInstanceUid) return
    if (typeof this.props.onOpenLocal === 'function') {
      this.props.onOpenLocal(String(item.studyInstanceUid), item)
    }
  }

  renderItemPrimary(item) {
    const date = formatDate(item.studyDate)
    const mod = item.modality || '—'
    const desc = item.description || t('worklist.noDescription')
    const tags = []
    if (this.isCurrentItem(item)) tags.push(t('worklist.current'))
    if (this.isInLocal(item)) tags.push(t('worklist.inLocal'))
    const suffix = tags.length ? ` · ${tags.join(' · ')}` : ''
    return `${date} · ${mod} · ${desc}${suffix}`
  }

  renderItemSecondary(item) {
    const parts = []
    if (item.patientName) parts.push(item.patientName)
    else if (item.patientId) parts.push(item.patientId)
    if (item.clinicName) parts.push(item.clinicName)
    return parts.length ? parts.join(' · ') : null
  }

  renderItemRow = (item, keyPrefix, preferLocal) => {
    if (!item || !item.ticketId) return null
    const inLocal = this.isInLocal(item)
    return (
      <ListItem key={`${keyPrefix}-${item.ticketId}`} dense divider>
        <ListItemText
          primary={this.renderItemPrimary(item)}
          secondary={this.renderItemSecondary(item)}
          primaryTypographyProps={{ style: { fontSize: 13 } }}
          secondaryTypographyProps={{ style: UDV_SURFACE.muted }}
        />
        <ListItemSecondaryAction>
          {preferLocal && inLocal ? (
            <Button
              size="small"
              color="primary"
              onClick={() => this.handleOpenLocal(item)}
            >
              {t('worklist.openLocal')}
            </Button>
          ) : (
            <Button
              size="small"
              color="primary"
              onClick={() => this.handleOpen(item)}
            >
              {t('worklist.open')}
            </Button>
          )}
        </ListItemSecondaryAction>
      </ListItem>
    )
  }

  renderCola() {
    const items = Array.isArray(this.props.items) ? this.props.items : []
    const title = this.props.title
    if (!items.length) {
      return (
        <Typography variant="body2" style={{ ...UDV_SURFACE.muted, marginTop: 12 }}>
          {t('worklist.colaEmpty')}
        </Typography>
      )
    }
    return (
      <>
        {title ? (
          <Typography
            variant="body2"
            style={{ ...UDV_SURFACE.muted, marginTop: 8, marginBottom: 4 }}
          >
            {title}
          </Typography>
        ) : null}
        <List dense disablePadding>
          {items.map((it, i) => this.renderItemRow(it, `cola-${i}`, false))}
        </List>
      </>
    )
  }

  toggleExpand = (worklistId) => {
    this.setState((prev) => ({
      expandedId: prev.expandedId === worklistId ? null : worklistId,
    }))
  }

  renderRecientes() {
    const sessions = Array.isArray(this.props.sessions) ? this.props.sessions : []
    if (!sessions.length) {
      return (
        <Typography variant="body2" style={{ ...UDV_SURFACE.muted, marginTop: 12 }}>
          {t('worklist.recientesEmpty')}
        </Typography>
      )
    }
    const groups = groupWorklistSessionsByDay(sessions)
    const { expandedId } = this.state
    return (
      <div style={{ marginTop: 8 }}>
        {groups.map((g) => (
          <div key={g.label} style={{ marginBottom: 12 }}>
            <Typography
              variant="caption"
              style={{
                ...UDV_SURFACE.muted,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}
            >
              {g.label === 'Hoy'
                ? t('worklist.dayToday')
                : g.label === 'Ayer'
                  ? t('worklist.dayYesterday')
                  : g.label}
            </Typography>
            {g.sessions.map((sess) => {
              const id = sess.worklistId
              const open = expandedId === id
              const items = Array.isArray(sess.items) ? sess.items : []
              return (
                <div key={id} style={{ marginTop: 4 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      borderBottom: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => this.toggleExpand(id)}
                      aria-label={t('worklist.expand')}
                    >
                      {open ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </IconButton>
                    <div style={{ flex: 1, minWidth: 0, padding: '6px 0' }}>
                      <Typography variant="body2" noWrap>
                        {formatTime(sess.receivedAt)} ·{' '}
                        {formatWorklistSessionTitle(sess)}
                      </Typography>
                      <Typography variant="caption" style={UDV_SURFACE.muted}>
                        {t('worklist.itemCount', {
                          count: sess.itemCount != null ? sess.itemCount : items.length,
                        })}
                      </Typography>
                    </div>
                  </div>
                  <Collapse in={open}>
                    <List dense disablePadding>
                      {items.map((it, i) =>
                        this.renderItemRow(it, `rec-${id}-${i}`, true)
                      )}
                    </List>
                  </Collapse>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    )
  }

  render() {
    const { open, onClose, tab, onTabChange, onManageStorage } = this.props
    const active = tab === 'recientes' ? 'recientes' : 'cola'

    return (
      <Dialog
        open={!!open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        {...udvDialogProps}
      >
        <DialogTitle style={UDV_SURFACE.title}>{t('worklist.title')}</DialogTitle>
        <DialogContent style={UDV_SURFACE.content}>
          <Tabs
            value={active}
            onChange={(_e, v) => {
              if (typeof onTabChange === 'function') onTabChange(v)
            }}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab value="cola" label={t('worklist.tabCola')} />
            <Tab value="recientes" label={t('worklist.tabRecientes')} />
          </Tabs>
          {active === 'cola' ? this.renderCola() : this.renderRecientes()}
        </DialogContent>
        <DialogActions style={UDV_SURFACE.actions}>
          {typeof onManageStorage === 'function' ? (
            <Button onClick={onManageStorage} style={{ marginRight: 'auto' }}>
              {t('worklist.manageStorage')}
            </Button>
          ) : null}
          <Button onClick={onClose} color="primary">
            {t('worklist.close')}
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
}

export default WorklistDlg
